import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { Button } from './ui/button';
import { Download, Loader2, TrendingUp, ShoppingBag, Radio, Sparkles } from 'lucide-react';
import { fetchEarnings, downloadEarningsCsv } from '../api/earnings';
import { fmtCredits } from '../lib/currency';
import { useToast } from '../hooks/use-toast';

const RANGES = [7, 30, 90];

// Phase 30.B — earnings dashboard is denominated in integer Credits.
const formatUsd = fmtCredits;
const formatTick = (d) => {
  if (!d) return '';
  const [, mm, dd] = d.split('-');
  return `${mm}/${dd}`;
};

const TooltipBox = ({ active, payload, label }) => {
  const { t } = useTranslation('wallet');
  if (!active || !payload?.length) return null;
  const net = payload.find((p) => p.dataKey === 'net')?.value || 0;
  const gross = payload.find((p) => p.dataKey === 'gross')?.value || 0;
  return (
    <div className="rounded-md border border-border bg-popover/95 px-3 py-2 text-xs shadow-xl backdrop-blur">
      <p className="mb-1 font-semibold text-foreground">{label}</p>
      <p className="text-muted-foreground">{t('earnings.tt_net')}: <span className="gold-text font-semibold">{formatUsd(net)}</span></p>
      <p className="text-muted-foreground">{t('earnings.tt_gross')}: {formatUsd(gross)}</p>
    </div>
  );
};

const StatTile = ({ label, value, sub, icon: Icon, tooltip, testId }) => (
  <div
    className="rounded-lg border border-border bg-card/70 p-3"
    title={tooltip || undefined}
    data-testid={testId}
  >
    <div className="mb-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
      {Icon && <Icon className="h-3 w-3" />}
      {label}
    </div>
    <p className="text-lg font-semibold text-foreground">{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
  </div>
);

// Phase F9.6 — labels moved to i18n (wallet.earnings.*) for full IT/EN
// parity. Metrics, endpoints and formulas are UNCHANGED.
const EarningsDashboard = () => {
  const { t } = useTranslation('wallet');
  const { toast } = useToast();
  const [range, setRange] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchEarnings(range)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => {
        if (!cancelled) toast({ title: t('earnings.load_error'), variant: 'destructive' });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range, toast, t]);

  const handleCsv = async () => {
    setDownloading(true);
    try {
      await downloadEarningsCsv(range);
    } catch (e) {
      toast({ title: t('earnings.csv_error'), variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  const chartData = useMemo(() => {
    return (data?.points || []).map((p) => ({
      ...p,
      label: formatTick(p.date),
    }));
  }, [data]);

  const postBucket = data?.by_type?.post || { net: 0, gross: 0, fee: 0, count: 0 };
  const liveBucket = data?.by_type?.live || { net: 0, gross: 0, fee: 0, count: 0 };
  const tipBucket  = data?.by_type?.tip  || { net: 0, gross: 0, fee: 0, count: 0 };
  const postEarnings = postBucket.net || 0;
  const liveEarnings = liveBucket.net || 0;
  const tipEarnings  = tipBucket.net  || 0;
  const tipFmt = (b) => t('earnings.tooltip', {
    gross: formatUsd(b.gross), fee: formatUsd(b.fee), net: formatUsd(b.net),
  });

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="earnings-dashboard">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 gold-text" />
          <h2 className="text-sm font-semibold uppercase tracking-wide">{t('earnings.title')}</h2>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-border bg-background/60 p-0.5" role="tablist">
            {RANGES.map((r) => (
              <button
                key={r}
                role="tab"
                aria-selected={range === r}
                onClick={() => setRange(r)}
                className={`rounded px-2.5 py-1 text-xs font-medium transition ${
                  range === r
                    ? 'gold-gradient text-black'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`earnings-range-${r}`}
              >
                {t('earnings.range_days', { d: r })}
              </button>
            ))}
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={downloading || loading}
            onClick={handleCsv}
            className="h-7 gap-1.5 border-border"
            data-testid="earnings-csv-btn"
          >
            {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            CSV
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex h-48 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin gold-text" />
        </div>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-5">
            <StatTile
              testId="earnings-tile-net"
              label={t('earnings.net_label', { d: range })}
              value={formatUsd(data?.total_net)}
              sub={t('earnings.sales_count', { count: data?.total_count || 0 })}
              icon={TrendingUp}
              tooltip={t('earnings.tooltip', { gross: formatUsd(data?.total_gross), fee: formatUsd(data?.total_fee), net: formatUsd(data?.total_net) })}
            />
            <StatTile
              testId="earnings-tile-gross"
              label={t('earnings.gross_label', { d: range })}
              value={formatUsd(data?.total_gross)}
              sub={t('earnings.fee_sub', { fee: formatUsd(data?.total_fee) })}
              tooltip={t('earnings.tooltip', { gross: formatUsd(data?.total_gross), fee: formatUsd(data?.total_fee), net: formatUsd(data?.total_net) })}
            />
            <StatTile
              testId="earnings-tile-posts"
              label={t('earnings.from_posts')}
              value={formatUsd(postEarnings)}
              sub={t('earnings.sales_count', { count: postBucket.count || 0 })}
              icon={ShoppingBag}
              tooltip={tipFmt(postBucket)}
            />
            <StatTile
              testId="earnings-tile-lives"
              label={t('earnings.from_lives')}
              value={formatUsd(liveEarnings)}
              sub={t('earnings.tickets_count', { count: liveBucket.count || 0 })}
              icon={Radio}
              tooltip={tipFmt(liveBucket)}
            />
            <StatTile
              testId="earnings-tile-tips"
              label={t('earnings.tips_label')}
              value={formatUsd(tipEarnings)}
              sub={t('earnings.tips_count', { count: tipBucket.count || 0 })}
              icon={Sparkles}
              tooltip={tipFmt(tipBucket)}
            />
          </div>

          <div className="h-56 w-full" data-testid="earnings-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="netGold" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#E6C687" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#E6C687" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="grossFaint" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7c6f55" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#7c6f55" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#2a2a2e" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="label"
                  stroke="#7c7c80"
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                  minTickGap={20}
                />
                <YAxis
                  stroke="#7c7c80"
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v}`)}
                  width={38}
                />
                <Tooltip content={<TooltipBox />} cursor={{ stroke: '#E6C687', strokeOpacity: 0.3 }} />
                <Legend
                  iconType="circle"
                  wrapperStyle={{ fontSize: 11, color: '#a3a3a8' }}
                  formatter={(v) => (v === 'net' ? t('earnings.legend_net') : t('earnings.legend_gross'))}
                />
                <Area
                  type="monotone"
                  dataKey="gross"
                  stroke="#7c6f55"
                  strokeWidth={1.2}
                  strokeDasharray="3 3"
                  fill="url(#grossFaint)"
                />
                <Area
                  type="monotone"
                  dataKey="net"
                  stroke="#E6C687"
                  strokeWidth={2}
                  fill="url(#netGold)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {data?.total_count === 0 && (
            <p className="mt-3 text-center text-xs text-muted-foreground">
              {t('earnings.empty')}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default EarningsDashboard;
